import { ArrowLeft, CheckCircle2, RefreshCw, TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";
import type { ApiClient, TaskDetailResponse } from "../api.js";
import { RecognitionReview } from "../components/RecognitionReview.js";
import { TaskStatus } from "../components/TaskStatus.js";
import { taskFailurePresentation } from "../task-presentation.js";

export function TaskPage({
  api,
  taskId,
}: {
  api: ApiClient;
  taskId: string;
}) {
  const [data, setData] = useState<TaskDetailResponse | null>(null);
  const [receipt, setReceipt] = useState<Record<string, unknown> | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getTask(taskId).then(setData);
  }, [api, taskId]);
  useEffect(() => {
    if (data?.task.status === "SUCCEEDED") {
      api.receipt(taskId).then((value) => setReceipt(value.receipt));
    }
  }, [api, data?.task.status, taskId]);

  if (!data) {
    return (
      <main className="loading-page">
        <RefreshCw className="spin" />
        <p>正在恢复任务…</p>
      </main>
    );
  }

  async function reorganize(input: {
    sourceNote: string;
    transcript: string;
    fieldOverrides: Record<string, string | number>;
  }) {
    setBusy(true);
    try {
      setData(await api.reorganize(taskId, input));
    } finally {
      setBusy(false);
    }
  }

  async function confirm(changes: Record<string, unknown>) {
    if (!data) return;
    setBusy(true);
    try {
      let confirmationId = data.confirmationId;
      if (Object.keys(changes).length > 0) {
        const revised = await api.revise(taskId, changes);
        setData(revised);
        confirmationId = revised.confirmationId;
      }
      if (!confirmationId) return;
      await api.confirm(taskId, confirmationId);
      setData(await api.getTask(taskId));
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    setBusy(true);
    try {
      await api.cancel(taskId);
      setData(await api.getTask(taskId));
    } finally {
      setBusy(false);
    }
  }

  const draft = data.draft ?? {};
  const previewExplainsError =
    Boolean(data.preview) &&
    [
      "REQUIRED_FIELDS_MISSING",
      "PRODUCT_NOT_FOUND",
      "MODEL_REJECTED",
      "PROCESSING_FAILED",
    ].includes(data.task.lastErrorCode ?? "");
  const failure =
    !previewExplainsError &&
    (data.task.status === "FAILED" ||
    (data.task.status === "NEEDS_INPUT" && Boolean(data.task.lastErrorCode)))
      ? taskFailurePresentation(data.task.lastErrorCode)
      : null;
  const canReview = ["NEEDS_INPUT", "AWAITING_CONFIRMATION"].includes(
    data.task.status,
  );

  return (
    <div className="app-shell detail-shell">
      <header className="detail-header">
        <a href="/" aria-label="返回首页">
          <ArrowLeft />
        </a>
        <div>
          <p className="eyebrow">TASK RECEIPT</p>
          <h1>任务详情</h1>
        </div>
        <TaskStatus status={data.task.status} />
      </header>
      <main className="page-content">
        <section className="task-identity">
          <span>任务名称</span>
          <strong>{data.task.displayTitle ?? "样品资料整理"}</strong>
          <p>提交人 · {data.task.submitterDisplayName}</p>
          <details>
            <summary>查看技术编号</summary>
            <code>{taskId}</code>
          </details>
        </section>

        {canReview && data.preview ? (
          <RecognitionReview
            taskId={taskId}
            draft={draft}
            preview={data.preview}
            missing={data.missing}
            confirmationId={data.confirmationId}
            onReorganize={reorganize}
            onConfirm={confirm}
            onCancel={cancel}
          />
        ) : null}

        {data.task.status === "AWAITING_CONFIRMATION" && !data.preview ? (
          <>
            <section className="review-card">
              <p className="eyebrow">AGENT SUMMARY</p>
              <h2>{String(draft.productName ?? "待确认任务")}</h2>
              <dl>
                <div>
                  <dt>本次动作</dt>
                  <dd>{String(draft.action ?? "—")}</dd>
                </div>
                <div>
                  <dt>提炼结论</dt>
                  <dd>{String(draft.extractedConclusion ?? "—")}</dd>
                </div>
              </dl>
            </section>
            <button
              className="primary-button"
              disabled={busy || !data.confirmationId}
              onClick={() => confirm({})}
            >
              确认并写入样品系统
            </button>
          </>
        ) : null}

        {data.task.status === "SUCCEEDED" ? (
          <section className="success-card">
            <CheckCircle2 />
            <p className="eyebrow">COMPLETED</p>
            <h2>任务已完成</h2>
            <p>样品记录：{String(receipt?.sampleRecordId ?? "正在读取")}</p>
            <a className="secondary-button" href="/tasks">
              返回任务中心
            </a>
          </section>
        ) : null}

        {failure ? (
          <section className="failure-card">
            <TriangleAlert />
            <h2>{failure.summary}</h2>
            <p>{failure.nextStep}</p>
          </section>
        ) : null}

        {data.task.status === "NEEDS_INPUT" && !data.preview ? (
          <section className="review-actions" aria-label="任务恢复操作">
            <a className="secondary-button" href="/submit">
              重新提交资料
            </a>
            <button
              className="cancel-task-button"
              disabled={busy}
              onClick={cancel}
            >
              取消这个任务
            </button>
          </section>
        ) : null}

        {["PROCESSING", "EXECUTING", "UNKNOWN"].includes(data.task.status) ? (
          <section className="progress-card">
            <RefreshCw className="spin" />
            <h2>任务仍在继续</h2>
            <p>关闭页面也不会取消，可以稍后从任务中心回来查看。</p>
          </section>
        ) : null}
      </main>
    </div>
  );
}
